/**
 * Модальное окно с затемнением фона, поддержкой размеров и footer
 * Автоматически блокирует скролл body при открытии
 */
import { ReactNode, useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  testId?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnBackdropClick?: boolean;
}

const sizes = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Modal({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  footer,
  testId,
  size = 'md',
  closeOnBackdropClick = true,
}: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={closeOnBackdropClick ? onClose : undefined}
    >
      <div className="fixed inset-0 bg-shell-strong/55 backdrop-blur-sm" />
      <div
        className={cn(
          'relative shell-card w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col',
          sizes[size]
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="flex items-center justify-between p-6 border-b border-shell-border">
            <h2 className="text-xl font-bold text-shell-strong">{title}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-shell-hover rounded-xl text-shell-muted hover:text-shell-text transition-colors"
              data-testid={`${testId}-close`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-6 text-shell-text">{children}</div>
        {footer && (
          <div className="border-t border-shell-border p-6 bg-shell-elevated/40">{footer}</div>
        )}
      </div>
    </div>
  );
}

